import { businessData as businessDataAtom } from 'atoms';
import moment from 'moment';
import OutOfOfficePopup from 'pages/Business/OutOfOffice/OutOfOfficePopup';
import InnerPageLayout from 'pages/Business/Partials/InnerPageLayout';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';
import { toasterNotification } from 'utils/toaster';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';

import EmptyOutOfOffice from './EmptyOutOfOffice';
import OutOfOfficeTable from './OutOfOfficeTable';
import Loader from 'common/Loaders/Loader';

const DataContainer = styled.div`
    background: ${(props) => props.theme.colorBackground};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    flexgrow: 1;
`;

// Utility function to format dates correctly by applying a -5 hour offset
// to compensate for the timezone difference in display
const formatDateWithOffset = (dateString) => {
    if (!dateString) return '';
    
    // Create a moment object from the date string
    const date = moment(dateString);
    
    // Check if the date is valid
    if (!date.isValid()) {
        return 'Invalid Date';
    }
    
    // Subtract 5 hours to fix the timezone display issue
    date.subtract(6, 'hours').add('30', 'minutes');
    
    // Format the date with the fixed time
    return date.format('MMM Do YYYY @ h:mm:ss a');
};

const OutOfOfficeHome = () => {
    const { token, loading: authLoading } = useAuth();
    const [loading, setLoading] = useState(true);
    const [openPopup, setOpenPopup] = useState(false);
    const [oooList, setOooList] = useState([]);
    const [rows, setRows] = useState([]);
    const [selectedOoo, setSelectedOoo] = useState(null);
    const [providers, setProviders] = useState([]);

    const [business, setBusiness] = useRecoilState(businessDataAtom);

    const getOooList = async () => {
        try {
            // Validate business_id
            if (!business?.id) {
                console.error('Business ID is required');
                setOooList([]);
                return;
            }

            // Validate access token (only show error if auth is loaded and token is still missing)
            if (authLoading) {
                // Still loading, wait for auth to complete
                setOooList([]);
                return;
            }
            
            if (!token) {
                console.error('No authentication token found');
                toasterNotification('error', '', 'Authentication error. Please try again.');
                setOooList([]);
                return;
            }

            // Fetch provider OOO list and business OOO list in parallel
            const [providerResponse, businessResponse] = await Promise.all([
                fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-ooo-list`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ business_id: business.id })
                    }
                ),
                fetch(
                    `${process.env.REACT_APP_SERVER_URL}/api/providers/business-ooo-list`,
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ business_id: business.id })
                    }
                )
            ]);

            const providerResult = await providerResponse.json();
            const businessResult = await businessResponse.json();

            // Check for errors in provider OOO list
            if (!providerResponse.ok || providerResult.error) {
                const errorMessage = providerResult.error || 'Failed to fetch provider out of office list';
                console.error('Error fetching provider OOO list:', errorMessage);
                toasterNotification('error', '', errorMessage);
            }

            // Check for errors in business OOO list
            if (!businessResponse.ok || businessResult.error) {
                const errorMessage = businessResult.error || 'Failed to fetch business out of office list';
                console.error('Error fetching business OOO list:', errorMessage);
                toasterNotification('error', '', errorMessage);
            }

            // Preserve object structure - do not convert to array
            // Combine both lists
            const providerOooData = providerResult.data;
            const businessOooData = businessResult.data;
            const providerOooList = Array.isArray(providerOooData) ? providerOooData : (providerOooData ? [providerOooData] : []);
            const businessOooList = Array.isArray(businessOooData) ? businessOooData : (businessOooData ? [businessOooData] : []);
            const combinedOooList = [...providerOooList, ...businessOooList];

            // Set the combined OOO list
            setOooList(combinedOooList);
        } catch (error) {
            console.error('Error in getOooList:', error);
            toasterNotification('error', '', 'An unexpected error occurred. Please try again.');
            setOooList([]);
        }
    };

    const getOoo = async (id) => {
        // Use already-fetched OOO list from backend instead of making a new API call
        if (!id) {
            console.error('Out-of-office ID is required');
            return;
        }

        const found = (oooList || []).find((ooo) => ooo.id === id);

        if (!found) {
            console.warn('No out-of-office entry found for id:', id);
            setSelectedOoo(null);
            return;
        }

        setSelectedOoo(found);
    };

    const getProviders = async () => {
        try {
            // Validate business_id
            if (!business?.id) {
                console.error('Business ID is required');
                setProviders([]);
                return;
            }

            // Validate access token
            if (!token) {
                console.error('No authentication token found');
                setProviders([]);
                return;
            }

            // Fetch providers from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/provider-list`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        business_id: business.id,
                        status_not_in: ['temp', 'deleted']
                    })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch providers';
                console.error('Error fetching providers:', errorMessage);
                setProviders([]);
                return;
            }

            // Backend returns { success: true, data: [...] } or data array directly
            const providerData = result.success ? (result.data || []) : (result || []);
            const providersList = Array.isArray(providerData) ? providerData : (providerData ? [providerData] : []);
            
            setProviders(providersList);
        } catch (error) {
            console.error('Error fetching providers:', error);
            setProviders([]);
        }
    };

    useEffect(() => {
        if (business?.id) {
            getProviders().then(() => {
                getOooList().then(() => {
                    setLoading(false);
                });
            });
        }
    }, [business?.id]);

    useEffect(() => {
        if (oooList?.length > 0) {
            let rows = oooList
                .map((ooo) => {
                    let providerData = providers?.find((p) => p.id === ooo.provider_id);
                    
                    // Format dates with our offset function to fix timezone issue
                    const fromDate = formatDateWithOffset(ooo?.date_start);
                    const toDate = formatDateWithOffset(ooo?.date_end);

                    // Handle business-level entries (when provider_id is null)
                    let providerName;
                    if (!ooo.provider_id) {
                        providerName = business?.name || 'Business';
                    } else if (providerData) {
                        providerName = (providerData?.first_name || '') + ' ' + (providerData?.last_name || '');
                    } else {
                        // Skip entries with invalid provider_id
                        return null;
                    }
                    
                    return {
                        id: ooo?.id,
                        provider: providerName,
                        from: fromDate,
                        to: toDate
                    };
                })
                .filter(Boolean);
            setRows(rows);
        } else {
            setRows([]);
        }
    }, [oooList, providers, business]);

    const togglePopup = (e) => {
        if (e?.row) {
            getOoo(e.row.id).then();
        } else {
            setSelectedOoo(null);
        }

        setOpenPopup(!openPopup);
    };

    return (
        <div className="h-full bg-[#F5F1ED]" style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
            {loading ? (
                <Loader />
            ) : (
                <>
                    {oooList?.length === 0 && <EmptyOutOfOffice togglePopup={togglePopup}></EmptyOutOfOffice>}

                    {oooList?.length > 0 && (
                        <DataContainer>
                            <InnerPageLayout
                                heading="Out of Office Schedule"
                                togglePopup={togglePopup}
                                btnText="Schedule an Out of Office"
                                buttonType="secondary"
                            >
                                <OutOfOfficeTable 
                                    rows={rows} 
                                    togglePopup={togglePopup}
                                />
                            </InnerPageLayout>
                        </DataContainer>
                    )}
                </>
            )}

            <OutOfOfficePopup
                open={openPopup}
                onClose={togglePopup}
                getOooList={getOooList}
                selectedOoo={selectedOoo}
                providers={providers}
                getProviders={getProviders}
            ></OutOfOfficePopup>
        </div>
    );
};

export default OutOfOfficeHome;
